import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { api, unwrap } from "@/shared/lib/apiClient";
import { overviewKeys } from "@/shared/lib/queryKeys";
import type { UpdateUsersRequest } from "../roster.types";
import { rosterKeys } from "./roster.queries";

// Invalidate users AND every overview window (names/roles/classes surface there too). Return the
// users refetch promise so RosterPage's clear (mutate-level onSuccess) runs only after fresh data
// lands — clearing mid-flight would flash the stale value.
function invalidateRosterAndOverview(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: overviewKeys.all });
  return queryClient.invalidateQueries({ queryKey: rosterKeys.users });
}

export function useSaveRoster() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateUsersRequest) =>
      unwrap(await api.PUT("/api/users", { body })),
    onSuccess: () => invalidateRosterAndOverview(queryClient),
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(await api.DELETE("/api/users/{id}", { params: { path: { id } } })),
    onSuccess: () => invalidateRosterAndOverview(queryClient),
  });
}

// Revoking sessions changes no roster field, but refresh the list to reflect server state.
export function useResetPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(
        await api.POST("/api/users/{id}/reset-password", {
          params: { path: { id } },
        }),
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: rosterKeys.users }),
  });
}
